import React, { useState, useEffect } from 'react';
import { Typography, Card, Spin, message } from 'antd';
import { CheckCircleOutlined, RocketOutlined, RightOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { THEME_COLORS, COLORS } from '../../../utils/colors';

const { Title, Text } = Typography;
const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';

const FinishStep = ({ formData }) => {
  const history = useHistory();
  const { getToken } = useAuth();
  const [isProcessing, setIsProcessing] = useState(true);
  const [showSummary, setShowSummary] = useState(false);
  const [hasTaskId, setHasTaskId] = useState(false);
  const [taskComplete, setTaskComplete] = useState(false);

  useEffect(() => {
    if (formData) {
      // Check if we have a task_id from various possible locations
      const taskId = formData.task_id ||
                     formData.apiResponse?.task_id ||
                     formData.apiResponse?.fast_task_id ||
                     formData.apiResponse?.data?.task_id;
      const hasTask = !!taskId;
      setHasTaskId(hasTask);

      // Show celebratory screen immediately; skip "Finalizing Your Setup" loading step
      setIsProcessing(false);
      setShowSummary(true);

      if (hasTask && taskId) {
        console.log('FinishStep: Polling task-status in background with task_id:', taskId);
        pollTaskStatus(taskId);
      }
    }
  }, [formData]);

  const pollTaskStatus = async (taskId) => {
    let retryCount = 0;
    const maxRetries = 120; // ~10 minutes max (5 seconds * 120)

    const pollStatus = async () => {
      try {
        retryCount += 1;
        
        console.log(`FinishStep: Polling task-status API (attempt ${retryCount}) for task_id:`, taskId);
        const token = await getToken({ template: 'default' });
        const url = `${BASE_URL}/dealership/task-status/${taskId}`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch task status');
        }

        const result = await response.json();

        // Check if task is complete (regardless of task_type)
        if (result.is_complete === true) {
          // Task completed successfully
          setTaskComplete(true);
          setIsProcessing(false);
          setShowSummary(true);
        } else if (result.is_complete === false) {
          // Task still in progress, continue polling
          if (retryCount >= maxRetries) {
            setIsProcessing(false);
            message.error('Task timed out. Please contact support.');
            setShowSummary(true);
          } else {
            // Continue polling - schedule next poll (every 5 seconds)
            setTimeout(() => {
              pollStatus();
            }, 5000);
          }
        } else {
          // Unknown state, check status as fallback
          const status = result.status?.toUpperCase();
          if (status === 'SUCCESS') {
            setTaskComplete(true);
            setIsProcessing(false);
            setShowSummary(true);
          } else if (status === 'FAILURE' || status === 'REVOKED') {
            setIsProcessing(false);
            message.error('Failed to customize dashboard. Please contact support.');
            setShowSummary(true);
          } else {
            // Continue polling if we haven't exceeded max retries
            if (retryCount >= maxRetries) {
              setIsProcessing(false);
              message.error('Task timed out. Please contact support.');
              setShowSummary(true);
            } else {
              setTimeout(() => {
                pollStatus();
              }, 5000);
            }
          }
        }
      } catch (error) {
        console.error("Polling error:", error);
        message.error('Failed to check task status. Please try again.');
        setIsProcessing(false);
        setShowSummary(true);
      }
    };

    // Start polling immediately (first poll happens right away)
    pollStatus();
  };

  if (isProcessing && !formData) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <div style={{ marginBottom: 32 }}>
          <RocketOutlined style={{ fontSize: 64, color: COLORS.PRIMARY[500], marginBottom: 16 }} />
          <Title level={2} style={{ color: THEME_COLORS.DARK.text.primary }}>
            {hasTaskId ? 'Creating Your Market Insights' : 'Finalizing Your Setup'}
          </Title>
          <Text style={{ color: THEME_COLORS.DARK.text.secondary, fontSize: 16 }}>
            {hasTaskId 
              ? 'You\'re the first dealer in your area! We\'re creating personalized insights for your market...'
              : 'Please wait while we complete your onboarding...'
            }
          </Text>
        </div>

        <Card style={{ 
          maxWidth: 500, 
          margin: '0 auto', 
          marginBottom: 32,
          background: THEME_COLORS.DARK.bg.secondary,
          border: `1px solid ${THEME_COLORS.DARK.border.secondary}`,
          color: THEME_COLORS.DARK.text.primary
        }}>
          <div style={{ marginBottom: 24 }}>
            <Spin size="large" />
          </div>
          
          <Text style={{ color: THEME_COLORS.DARK.text.secondary }}>
            {hasTaskId 
              ? 'Processing marketplace data and generating AI-powered insights for your city. This may take a few minutes...'
              : 'Setting up your dealership profile and configuring your dashboard...'
            }
          </Text>
        </Card>
      </div>
    );
  }

  if (showSummary || formData) {
    const isBuilding = hasTaskId && !taskComplete;

    return (
      <div className="onboarding-finish-wrap">
        <div className="onboarding-finish-bg" aria-hidden="true" />
        <div className="onboarding-finish-card">
          {isBuilding ? (
            <>
              <div className="onboarding-finish-hero-icon onboarding-finish-hero-icon--building">
                <Spin size="large" />
              </div>
              <h1 className="onboarding-finish-title">We're Building Your Account</h1>
              <p className="onboarding-finish-subtitle">
                Setting up your dashboard and market insights. This usually takes a minute or two.
              </p>
              <div className="onboarding-finish-confirmed">
                <div className="onboarding-finish-confirmed-item">
                  <CheckCircleOutlined />
                  <span>Dealership Connected</span>
                </div>
                <div className="onboarding-finish-confirmed-item">
                  <CheckCircleOutlined />
                  <span>Payment Activated</span>
                </div>
                <div className="onboarding-finish-confirmed-item">
                  <Spin size="small" />
                  <span>Building your dashboard…</span>
                </div>
              </div>
              <button type="button" className="onboarding-finish-cta-primary" disabled>
                <Spin size="small" style={{ marginRight: 8 }} />
                Go to My Dashboard
              </button>
              <button type="button" className="onboarding-finish-cta-secondary" disabled>
                <Spin size="small" style={{ marginRight: 8 }} />
                Analyze Your First VIN
              </button>
            </>
          ) : (
            <>
              <div className="onboarding-finish-hero-icon">
                <CheckCircleOutlined />
              </div>
              <h1 className="onboarding-finish-title">You're Live on Hooptie Elite</h1>
              <p className="onboarding-finish-subtitle">
                Your dealership is now powered by AI-driven market intelligence.
              </p>
              <div className="onboarding-finish-confirmed">
                <div className="onboarding-finish-confirmed-item">
                  <CheckCircleOutlined />
                  <span>Dealership Connected</span>
                </div>
                <div className="onboarding-finish-confirmed-item">
                  <CheckCircleOutlined />
                  <span>Payment Activated</span>
                </div>
                <div className="onboarding-finish-confirmed-item">
                  <CheckCircleOutlined />
                  <span>AI Dashboard Ready</span>
                </div>
              </div>
              <button
                type="button"
                className="onboarding-finish-cta-primary"
                onClick={() => history.push('/dashboard')}
              >
                <RocketOutlined />
                Go to My Dashboard
              </button>
              <button
                type="button"
                className="onboarding-finish-cta-secondary"
                onClick={() => history.push('/search')}
              >
                Analyze Your First VIN
                <RightOutlined />
              </button>
            </>
          )}

          <p className="onboarding-finish-footer">Used by independent dealers nationwide.</p>
        </div>
      </div>
    );
  }

  return null;
};

export default FinishStep;
